// Project + account settings. Two-pane modal: left rail nav + right
// content pane. Sections:
//   - General      pack swap, delete project
//   - Integrations MCP server (read external AI tools' access)
//   - API Tokens   long-lived bearer tokens for CLI / external MCP
//   - Account      identity + sign-out, admin panel link for admins
//
// Real usage / billing lives on the admin page — there's no point
// duplicating a placeholder block here.

import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import i18n, { detectBrowserLocale, type Locale } from '../i18n';
import type {
  ApiToken,
  CreateApiTokenResponse,
  McpStatus,
  MeResponse,
  PackDef,
  Project,
  WalletStatus,
  WalletTransaction,
} from '../api';
import { api } from '../api';
import { TopupModal } from './TopupModal';
import { useIsMobile } from '../hooks/useIsMobile';

type LocaleChoice = Locale | 'auto';

export type SettingsSection =
  | 'general'
  | 'integrations'
  | 'tokens'
  | 'billing'
  | 'invites'
  | 'account';

interface Props {
  project: Project | null;
  /** Called after a successful pack swap so the parent can refresh state.
   *  Receives the freshly-mutated project row. */
  onPackChanged: (project: Project) => void;
  /** Called after a successful delete. The parent should drop the project
   *  from its list and switch focus elsewhere; the modal closes itself. */
  onProjectDeleted: (projectId: string) => void;
  onClose: () => void;
  /** Authed user — drives the Account section (read-only identity).
   *  Null when /api/me is in flight; the Account section renders a
   *  loading hint in that case. */
  me: MeResponse | null;
  /** Sign-out handler — same one the sidebar uses. Called from the
   *  Account section's primary action. */
  onLogout: () => void | Promise<void>;
  /** Open the unified admin page. Null when the current user isn't
   *  an admin (so the Account section hides the link). */
  onOpenAdmin?: (() => void) | null;
  /** Optional initial tab to land on. Sidebar avatar opens 'account';
   *  SETTINGS row opens 'general' (default); MCP chip opens 'integrations'.
   *  Falls back to 'account' if 'general' is requested without a project. */
  initialSection?: SettingsSection;
}

// Section id → i18n key for its nav label. Labels are resolved through
// `t()` inside the component so they react to the active locale.
const NAV_ITEMS: ReadonlyArray<readonly [SettingsSection, string]> = [
  ['general', 'settings.navGeneral'],
  ['integrations', 'settings.navIntegrations'],
  ['tokens', 'settings.navTokens'],
  ['billing', 'settings.navBilling'],
  ['invites', 'settings.navInvites'],
  ['account', 'settings.navAccount'],
];

export function SettingsModal({
  project,
  onPackChanged,
  onProjectDeleted,
  onClose,
  me,
  onLogout,
  onOpenAdmin,
  initialSection,
}: Props) {
  const { t } = useTranslation();
  // Active nav section. Honours `initialSection` (caller's intent) but
  // gracefully falls back when 'general' / 'integrations' are asked for
  // without a project loaded — those tabs render a "no project" hint
  // and would feel like dead clicks.
  const [section, setSection] = useState<SettingsSection>(() => {
    const want = initialSection ?? (project ? 'general' : 'account');
    if ((want === 'general' || want === 'integrations') && !project) {
      return 'account';
    }
    return want;
  });
  // Pack catalog is small and rarely-changing; fetch on mount and live with
  // the one-shot. App could pre-load and pass down but that's plumbing for
  // a list that's static enough to cache trivially here.
  const [packs, setPacks] = useState<PackDef[]>([]);
  useEffect(() => {
    api.listPacks().then(setPacks).catch(() => {});
  }, []);
  const [packDraft, setPackDraft] = useState<string>(project?.pack ?? 'blank');
  const [savingPack, setSavingPack] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MCP status. Fetched on mount + on project change. Null while loading or
  // if the fetch fails (the section then shows a "couldn't reach status"
  // hint instead of fake-zero numbers).
  const [mcpStatus, setMcpStatus] = useState<McpStatus | null>(null);
  const [togglingMcp, setTogglingMcp] = useState(false);
  // Tracks which copy-button just flashed "✓ copied". Keyed by string so
  // we can distinguish project-id from install-command from per-token
  // raw-secret without an enum that grows on every new use site.
  const [copied, setCopied] = useState<string | null>(null);
  // Two-stage delete: first click sets confirm=true and reveals a destructive
  // "really delete" button. Second click actually fires. Single-click delete
  // for an irreversible operation is not the kind of risk we should take.
  const [confirm, setConfirm] = useState(false);
  // Esc-to-close. Skipped while a destructive action is in flight to avoid
  // a half-finished delete leaving the user disoriented.
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Phone-shaped viewports: drop the left-rail nav (168px ate half the
  // modal width on a 375px screen), make Panel full-bleed, and swap the
  // rail for a horizontal pill row at the top of the body.
  const isMobile = useIsMobile();

  useEffect(() => {
    setPackDraft(project?.pack ?? 'blank');
    setConfirm(false);
    setError(null);
  }, [project?.id, project?.pack]);

  // (Re)load MCP status when the modal's project changes. Best-effort —
  // if the endpoint errors (DB down, server old), leave status null and
  // the panel renders a graceful fallback.
  useEffect(() => {
    if (!project?.id) { setMcpStatus(null); return; }
    let cancelled = false;
    api.getMcpStatus(project.id)
      .then((s) => { if (!cancelled) setMcpStatus(s); })
      .catch(() => { if (!cancelled) setMcpStatus(null); });
    return () => { cancelled = true; };
  }, [project?.id]);

  const toggleMcp = async () => {
    if (!project || !mcpStatus) return;
    setTogglingMcp(true);
    try {
      const res = await api.setMcpEnabled(project.id, !mcpStatus.enabled);
      // Patch the local snapshot rather than re-fetching — feels snappier
      // and the only field that changed is `enabled`.
      setMcpStatus({ ...mcpStatus, enabled: res.enabled });
    } catch (e) {
      setError(t('settings.errMcpToggle', { error: (e as Error).message }));
    } finally {
      setTogglingMcp(false);
    }
  };

  const copyToClipboard = async (text: string, kind: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      // Reset the "copied" pill after 1.5s so a second copy still gives a
      // visual flash.
      setTimeout(() => setCopied((cur) => (cur === kind ? null : cur)), 1500);
    } catch {
      // Clipboard API failures (insecure context, perm denied) — fall
      // back to a hint so the user can copy manually.
      setError(t('settings.errClipboard'));
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !savingPack && !deleting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, savingPack, deleting]);

  // Whether the General tab has actionable content for this user.
  // Without a project, only Account remains useful.
  const hasProject = project !== null;
  const packChanged = packDraft !== (project?.pack ?? 'blank');

  const savePack = async () => {
    if (!project || !packChanged) return;
    setError(null);
    setSavingPack(true);
    try {
      // Wire convention: empty string clears back to NULL. The dropdown
      // surfaces "blank" as a label; translate before sending.
      const updated = await api.patchProject(project.id, {
        pack: packDraft === 'blank' ? '' : packDraft,
      });
      onPackChanged(updated);
    } catch (e) {
      setError(t('settings.errUpdatePack', { error: (e as Error).message }));
    } finally {
      setSavingPack(false);
    }
  };

  const doDelete = async () => {
    if (!project) return;
    setError(null);
    setDeleting(true);
    try {
      await api.deleteProject(project.id);
      onProjectDeleted(project.id);
    } catch (e) {
      setError(t('settings.errDelete', { error: (e as Error).message }));
      setDeleting(false);
      setConfirm(false);
    }
  };

  return (
    <Backdrop onClose={onClose} isMobile={isMobile}>
      <Panel containerRef={containerRef} onClose={onClose} isMobile={isMobile}>
        <div
          style={{
            display: 'flex',
            // Mobile: stack nav row above content. Desktop: side-by-side.
            flexDirection: isMobile ? 'column' : 'row',
            minHeight: 0,
            flex: 1,
          }}
        >
          {/* Nav. Desktop: left rail. Mobile: horizontal scrollable pill row
              under the header. */}
          <nav
            style={
              isMobile
                ? {
                    flex: '0 0 auto',
                    borderBottom: '1px solid var(--rule)',
                    background: 'var(--paper-1)',
                    padding: '8px 10px',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 6,
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }
                : {
                    flex: '0 0 168px',
                    borderRight: '1px solid var(--rule)',
                    background: 'var(--paper-1)',
                    padding: '14px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }
            }
          >
            {NAV_ITEMS.map(([id, labelKey]) => {
              const active = section === id;
              const disabled = id === 'general' && !hasProject;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => !disabled && setSection(id)}
                  disabled={disabled}
                  style={{
                    textAlign: isMobile ? 'center' : 'left',
                    background: active ? 'white' : 'transparent',
                    border: '1px solid',
                    borderColor: active ? 'var(--rule)' : 'transparent',
                    color: disabled
                      ? 'var(--ink-4)'
                      : active
                        ? 'var(--ink-1)'
                        : 'var(--ink-2)',
                    padding: isMobile ? '6px 14px' : '6px 10px',
                    borderRadius: isMobile ? 999 : 4,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: 12.5,
                    fontWeight: active ? 600 : 400,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </nav>

          {/* Right content pane */}
          <div
            style={{
              flex: 1,
              // Tighter horizontal padding on mobile so we don't lose more
              // pixels to chrome on a narrow screen.
              padding: isMobile ? '14px 14px 12px' : '20px 24px 12px',
              overflow: 'auto',
              minWidth: 0,
            }}
          >
            {section === 'general' && project && (
              <GeneralSection
                project={project}
                packs={packs}
                packDraft={packDraft}
                onPackDraft={setPackDraft}
                packChanged={packChanged}
                savingPack={savingPack}
                onSavePack={savePack}
                deleting={deleting}
                confirm={confirm}
                onConfirm={() => setConfirm(true)}
                onCancelConfirm={() => setConfirm(false)}
                onDelete={doDelete}
              />
            )}
            {section === 'general' && !project && (
              <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
                {t('settings.noProjectGeneral')}
              </div>
            )}
            {section === 'integrations' && project && (
              <IntegrationsSection
                project={project}
                status={mcpStatus}
                toggling={togglingMcp}
                copied={copied}
                onToggleEnabled={toggleMcp}
                onCopy={copyToClipboard}
              />
            )}
            {section === 'integrations' && !project && (
              <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
                {t('settings.noProjectIntegrations')}
              </div>
            )}
            {section === 'tokens' && (
              <ApiTokensSection onCopy={copyToClipboard} copied={copied} />
            )}
            {section === 'billing' && <BillingSection me={me} />}
            {section === 'invites' && <InvitesSection />}
            {section === 'account' && (
              <AccountSection
                me={me}
                onLogout={onLogout}
                onOpenAdmin={onOpenAdmin}
                onClose={onClose}
              />
            )}

            {error && (
              <div
                style={{
                  marginTop: 14,
                  padding: '8px 10px',
                  fontSize: 12,
                  background: 'rgba(210, 63, 63, 0.08)',
                  color: 'var(--red, #d23f3f)',
                  borderRadius: 4,
                  border: '1px solid rgba(210, 63, 63, 0.2)',
                }}
              >
                {error}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            padding: '10px 22px',
            borderTop: '1px solid var(--rule)',
            background: 'var(--paper-1)',
            borderRadius: '0 0 8px 8px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            disabled={deleting}
            className="mono"
            style={{
              fontSize: 11,
              padding: '6px 14px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--ink-2)',
              cursor: deleting ? 'not-allowed' : 'pointer',
            }}
          >
            {t('common.close')}
          </button>
        </div>
      </Panel>
    </Backdrop>
  );
}

/** General tab body. Pulled out as its own component so the main
 *  modal render stays a tab-switch tree. Owns no state — drives
 *  everything off props. */
function GeneralSection({
  project,
  packs,
  packDraft,
  onPackDraft,
  packChanged,
  savingPack,
  onSavePack,
  deleting,
  confirm,
  onConfirm,
  onCancelConfirm,
  onDelete,
}: {
  project: Project;
  packs: PackDef[];
  packDraft: string;
  onPackDraft: (v: string) => void;
  packChanged: boolean;
  savingPack: boolean;
  onSavePack: () => void;
  deleting: boolean;
  confirm: boolean;
  onConfirm: () => void;
  onCancelConfirm: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section>
      <SectionLabel>{t('settings.sectionProject')}</SectionLabel>

      <Row label={t('settings.rowName')}>
        <span style={{ color: 'var(--ink-1)' }}>{project.name}</span>
        <span
          className="mono"
          style={{ marginLeft: 8, color: 'var(--ink-3)', fontSize: 10.5 }}
        >
          {t('settings.renameHint')}
        </span>
      </Row>

      <Row label={t('settings.rowPack')}>
        <select
          value={packDraft}
          onChange={(e) => onPackDraft(e.target.value)}
          disabled={savingPack || deleting}
          style={{
            fontSize: 12,
            padding: '4px 6px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: 'white',
            color: 'var(--ink-1)',
          }}
          title={packs.find((p) => p.id === packDraft)?.description ?? ''}
        >
          <option value="blank">{t('settings.packBlank')}</option>
          {packs
            .filter((p) => p.id !== 'blank')
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
        <button
          onClick={onSavePack}
          disabled={!packChanged || savingPack || deleting}
          className="mono"
          style={{
            marginLeft: 8,
            fontSize: 11,
            padding: '4px 10px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: packChanged ? 'var(--ink-0)' : 'var(--ink-4)',
            color: 'white',
            cursor: packChanged && !savingPack ? 'pointer' : 'not-allowed',
          }}
        >
          {savingPack ? t('settings.saving') : t('common.save')}
        </button>
      </Row>

      {/* Delete sits with the other project actions — no longer hidden
          under "DANGER ZONE" at the bottom where users miss it. The
          two-stage confirm is still the destructive guard. */}
      <Row label={t('common.delete')}>
        {confirm ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: 10,
              background: 'rgba(210, 63, 63, 0.05)',
              border: '1px solid rgba(210, 63, 63, 0.3)',
              borderRadius: 4,
            }}
          >
            <span
              style={{ fontSize: 12, color: 'var(--red, #d23f3f)' }}
            >
              {t('settings.deleteConfirm', { name: project.name })}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={onDelete}
                disabled={deleting}
                className="mono"
                style={{
                  fontSize: 11,
                  padding: '5px 12px',
                  border: '1px solid var(--red, #d23f3f)',
                  borderRadius: 4,
                  background: 'var(--red, #d23f3f)',
                  color: 'white',
                  cursor: deleting ? 'progress' : 'pointer',
                }}
              >
                {deleting ? t('settings.deleting') : t('settings.reallyDelete')}
              </button>
              <button
                onClick={onCancelConfirm}
                disabled={deleting}
                className="mono"
                style={{
                  fontSize: 11,
                  padding: '5px 12px',
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  background: 'white',
                  color: 'var(--ink-2)',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={onConfirm}
            className="mono"
            style={{
              fontSize: 11,
              padding: '4px 10px',
              border: '1px solid var(--red, #d23f3f)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--red, #d23f3f)',
              cursor: 'pointer',
            }}
          >
            {t('settings.deleteProject')}
          </button>
        )}
      </Row>
    </section>
  );
}

/** Account tab body. Read-only identity + sign-out + admin entry-point.
 *  Self-contained — pulls no extra data; everything comes from `me`
 *  which the parent has already fetched. */
/** PR-15 (i18n): user-wide language picker. Writes `users.locale` and
 *  applies the change to i18next immediately. "Auto" clears the override
 *  so the locale falls back to browser detection. */
function LanguageRow({ me }: { me: MeResponse }) {
  const { t } = useTranslation();
  const [value, setValue] = useState<LocaleChoice>(me.locale ?? 'auto');
  const [busy, setBusy] = useState(false);
  const apply = async (next: LocaleChoice) => {
    setValue(next);
    setBusy(true);
    try {
      await api.setUserLocale(next === 'auto' ? null : next);
      const effective = next === 'auto' ? detectBrowserLocale() : next;
      await i18n.changeLanguage(effective);
    } catch {
      // Low-stakes toggle — a failed persist just means it won't survive
      // reload; the in-session changeLanguage above still applied.
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <select
        value={value}
        disabled={busy}
        onChange={(e) => void apply(e.target.value as LocaleChoice)}
        style={{
          fontSize: 12.5,
          padding: '4px 10px',
          border: '1px solid var(--rule)',
          borderRadius: 4,
          background: 'white',
          color: 'var(--ink-1)',
          cursor: busy ? 'wait' : 'pointer',
        }}
      >
        <option value="auto">{t('settings.localeAuto')}</option>
        <option value="en">English</option>
        <option value="zh">中文</option>
        <option value="ja">日本語</option>
        <option value="de">Deutsch</option>
      </select>
      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink-3)' }}>
        {t('settings.languageHint')}
      </div>
    </div>
  );
}

function AccountSection({
  me,
  onLogout,
  onOpenAdmin,
  onClose,
}: {
  me: MeResponse | null;
  onLogout: () => void | Promise<void>;
  onOpenAdmin?: (() => void) | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [signingOut, setSigningOut] = useState(false);

  if (!me) {
    return (
      <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>
    );
  }

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await onLogout();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <section>
      <SectionLabel>{t('settings.signedInAs')}</SectionLabel>
      <Row label={t('settings.rowEmail')}>
        <span style={{ color: 'var(--ink-1)' }}>{me.email ?? '—'}</span>
        {!me.email_verified && (
          <span
            className="mono"
            style={{
              marginLeft: 8,
              fontSize: 10,
              color: 'var(--red, #d23f3f)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            {t('settings.unverified')}
          </span>
        )}
      </Row>
      {me.github_login && (
        <Row label={t('settings.rowGithub')}>
          <span style={{ color: 'var(--ink-1)' }}>@{me.github_login}</span>
        </Row>
      )}
      <Row label={t('settings.rowTenant')}>
        <span style={{ color: 'var(--ink-1)' }}>{me.tenant_name}</span>
        {me.is_admin && (
          <span
            className="mono"
            style={{
              marginLeft: 8,
              padding: '1px 6px',
              borderRadius: 3,
              background: 'var(--ink-0)',
              color: 'white',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            {t('settings.adminBadge')}
          </span>
        )}
      </Row>

      <div
        style={{
          marginTop: 18,
          paddingTop: 14,
          borderTop: '1px solid var(--rule)',
        }}
      >
        <SectionLabel>{t('settings.languageLabel')}</SectionLabel>
        <LanguageRow me={me} />
      </div>

      {onOpenAdmin && (
        <div
          style={{
            marginTop: 18,
            paddingTop: 14,
            borderTop: '1px solid var(--rule)',
          }}
        >
          <SectionLabel>{t('settings.adminTools')}</SectionLabel>
          <button
            onClick={() => {
              onClose();
              onOpenAdmin();
            }}
            className="mono"
            style={{
              fontSize: 11,
              padding: '5px 12px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--ink-1)',
              cursor: 'pointer',
            }}
          >
            {t('settings.openAdminPanel')} →
          </button>
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: 'var(--ink-3)',
            }}
          >
            {t('settings.adminToolsHint')}
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 18,
          paddingTop: 14,
          borderTop: '1px solid var(--rule)',
        }}
      >
        <SectionLabel>{t('settings.session')}</SectionLabel>
        <button
          onClick={handleLogout}
          disabled={signingOut}
          className="mono"
          style={{
            fontSize: 11,
            padding: '5px 12px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: 'white',
            color: 'var(--ink-1)',
            cursor: signingOut ? 'progress' : 'pointer',
          }}
        >
          {signingOut ? t('settings.signingOut') : t('settings.signOut')}
        </button>
      </div>

      {/* doc/07 PR-44: GDPR danger zone — irreversible account+data
          delete, gated behind typed confirmation. */}
      <DeleteAccountZone />
    </section>
  );
}

/** doc/07 PR-44: "Delete account" danger zone. Two-step: a button reveals
 *  a typed-confirmation field (must type DELETE) before the destructive
 *  call is enabled. On success the server has already revoked the session,
 *  so we hard-reload — the next /api/me 401s → login page. */
function DeleteAccountZone() {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const confirmWord = t('settings.deleteConfirmWord', { defaultValue: 'DELETE' });
  const canDelete = confirmText.trim().toUpperCase() === confirmWord.toUpperCase();

  async function doDelete() {
    if (!canDelete || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.deleteAccount();
      window.location.assign('/'); // session revoked server-side → login gate
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 24, paddingTop: 14, borderTop: '1px solid var(--rule)' }}>
      <div
        className="mono"
        style={{
          fontSize: 10,
          letterSpacing: '.14em',
          fontWeight: 700,
          color: 'var(--red, #d23f3f)',
          marginBottom: 8,
        }}
      >
        {t('settings.dangerZone', { defaultValue: 'DANGER ZONE' })}
      </div>
      {!armed ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5, flex: 1, minWidth: 220 }}>
            {t('settings.deleteAccountBlurb', {
              defaultValue:
                'Permanently delete your account and all data (projects, cards, threads, files). This cannot be undone — export first if you want a copy.',
            })}
          </span>
          <button
            type="button"
            onClick={() => setArmed(true)}
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: '.1em',
              padding: '5px 12px',
              border: '1px solid var(--red, #d23f3f)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--red, #d23f3f)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {t('settings.deleteAccount', { defaultValue: 'Delete account…' })}
          </button>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: 12.5, color: 'var(--ink-1)', lineHeight: 1.5, margin: '0 0 10px' }}>
            {t('settings.deleteConfirmPrompt', {
              defaultValue:
                'Type DELETE to confirm. This wipes everything immediately and signs you out.',
            })}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmWord}
              disabled={busy}
              style={{
                fontSize: 12.5,
                padding: '5px 10px',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background: 'white',
                color: 'var(--ink-1)',
                width: 140,
              }}
            />
            <button
              type="button"
              onClick={() => void doDelete()}
              disabled={!canDelete || busy}
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: '.1em',
                padding: '5px 12px',
                border: '1px solid var(--red, #d23f3f)',
                borderRadius: 4,
                background: canDelete && !busy ? 'var(--red, #d23f3f)' : 'var(--ink-4)',
                color: 'white',
                cursor: canDelete && !busy ? 'pointer' : 'not-allowed',
              }}
            >
              {busy
                ? t('settings.deleting', { defaultValue: 'Deleting…' })
                : t('settings.deletePermanently', { defaultValue: 'Permanently delete' })}
            </button>
            <button
              type="button"
              onClick={() => {
                setArmed(false);
                setConfirmText('');
                setErr(null);
              }}
              disabled={busy}
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: '.1em',
                padding: '5px 12px',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background: 'white',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>
          {err && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--red, #d23f3f)' }}>{err}</div>
          )}
        </div>
      )}
    </div>
  );
}

function Backdrop({
  onClose,
  isMobile,
  children,
}: {
  onClose: () => void;
  isMobile: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26, 24, 20, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1500,
        // Phone screens: drop the breathing room so the modal can go
        // full-bleed and we keep all the pixels for content.
        padding: isMobile ? 0 : 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}

function Panel({
  containerRef,
  onClose,
  isMobile,
  children,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  isMobile: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      ref={containerRef}
      style={{
        background: 'white',
        // Squared off + full-bleed on mobile so we don't lose corners to
        // viewport rounding; desktop keeps the floating card look.
        borderRadius: isMobile ? 0 : 8,
        width: isMobile ? '100vw' : 'min(820px, 94vw)',
        // Mobile: full viewport height. Desktop: capped so title stays
        // on screen while body scrolls internally.
        height: isMobile ? '100vh' : 'min(640px, 86vh)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0,0,0,0.25)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 22px',
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
        }}
      >
        <span className="serif" style={{ fontSize: 18, fontWeight: 500 }}>
          {t('settings.heading')}
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={onClose}
          className="mono"
          style={{
            fontSize: 12,
            color: 'var(--ink-3)',
            padding: '4px 10px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </div>
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mono"
      style={{
        fontSize: 10,
        color: 'var(--ink-3)',
        letterSpacing: '.1em',
        fontWeight: 700,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
        minHeight: 28,
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          width: 80,
          flexShrink: 0,
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, fontSize: 12.5, display: 'flex', alignItems: 'center' }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Settings → Integrations section. Surfaces MCP server status + the one-
 * time `claude mcp add` command pre-baked with the right env / binary
 * path / project_id. Hosted inside SettingsModal because per-project
 * controls live there; the actual binary lives in `rust/crates/draftlize-mcp/`.
 *
 * Visible fields:
 *   - status pill: connected (recent reads) / set up (no reads yet) /
 *     disabled (per-project toggle off) / unavailable (status fetch failed)
 *   - project URI: copy-able `draftlize://<project_id>` (handy for typing
 *     `@<URI>` references inside Claude Code)
 *   - install command: `claude mcp add draftlize -s user -e DATABASE_URL=…
 *     -- <binary>`. We don't know the user's repo path, so the binary
 *     path is left as a placeholder they fill in once.
 *   - disable toggle: writes through to projects.mcp_enabled
 *
 * Phase 2 hangs the recent-access log below this; Phase 3 adds the per-
 * card badges in CardsPanel (no UI here).
 */
/**
 * doc/12 §3.5.1: Settings → Integrations is the "Connect Claude Code"
 * wizard. Three steps on one page (no stepper state — every step stays
 * visible so the user can scan the whole path before starting):
 *
 *   Step 1  mint an API token inline (same POST as the API Tokens tab;
 *           the raw secret flashes once and is injected into step 2)
 *   Step 2  copy-paste CLI install + connect commands (cargo install →
 *           set-token → link <slug> → pull)
 *   Step 3  live connection status — polls `api_tokens.last_used_at`
 *           (bumped by BearerAuth on every CLI call) every 10 s and
 *           flips a gray "waiting" dot to green "connected · N min ago"
 *
 * The MCP stdio server needs direct Postgres access, which production
 * users don't have — so the old MCP setup block lives on as a collapsed
 * "local dev" path at the bottom, not the headline.
 */
function IntegrationsSection({
  project,
  status,
  toggling,
  copied,
  onToggleEnabled,
  onCopy,
}: {
  project: Project;
  status: McpStatus | null;
  toggling: boolean;
  copied: string | null;
  onToggleEnabled: () => void;
  onCopy: (text: string, kind: string) => void;
}) {
  const { t } = useTranslation();

  // ── wizard state ──────────────────────────────────────────────────
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [created, setCreated] = useState<CreateApiTokenResponse | null>(null);
  const [tokenName, setTokenName] = useState('claude-code');
  const [creating, setCreating] = useState(false);
  const [wzError, setWzError] = useState<string | null>(null);

  // Step 3 poller. 10 s keeps the "I just ran draftlize pull, did it
  // see me?" loop tight without hammering the server; the section
  // unmounts when the user switches tabs, which clears the interval.
  useEffect(() => {
    let alive = true;
    const tick = () => {
      api
        .listApiTokens()
        .then((rows) => {
          if (alive) setTokens(rows);
        })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const liveTokens = (tokens ?? []).filter((tok) => !tok.revoked_at);
  // Most-recently-used live token drives the status line.
  const lastUsedToken = liveTokens
    .filter((tok) => tok.last_used_at)
    .sort(
      (a, b) =>
        new Date(b.last_used_at as string).getTime() -
        new Date(a.last_used_at as string).getTime(),
    )[0] ?? null;
  const lastUsedMs = lastUsedToken
    ? Date.now() - new Date(lastUsedToken.last_used_at as string).getTime()
    : null;
  const connState: 'loading' | 'no-token' | 'waiting' | 'connected' | 'idle' =
    tokens === null
      ? 'loading'
      : liveTokens.length === 0
        ? 'no-token'
        : lastUsedMs === null
          ? 'waiting'
          : lastUsedMs < 24 * 3600 * 1000
            ? 'connected'
            : 'idle';

  const createToken = async () => {
    const name = tokenName.trim();
    if (!name || creating) return;
    setCreating(true);
    setWzError(null);
    try {
      const resp = await api.createApiToken(name);
      setCreated(resp);
      const rows = await api.listApiTokens().catch(() => null);
      if (rows) setTokens(rows);
    } catch (e) {
      setWzError(t('settings.errCreateToken', { error: (e as Error).message }));
    } finally {
      setCreating(false);
    }
  };

  // ── step 2 commands ───────────────────────────────────────────────
  const installCmd =
    'cargo install --git https://github.com/cooldk/draftlize.git draftlize-cli';
  const tokenCmd = `draftlize set-token ${created?.raw_token ?? t('settings.wizardTokenPlaceholder')}`;
  const linkCmd = `draftlize link ${project.slug}`;
  const pullCmd = 'draftlize pull';
  const allCmds = [installCmd, tokenCmd, linkCmd, pullCmd].join('\n');

  // Legacy local-dev MCP command (direct Postgres — dev machines only).
  const mcpCommand = [
    'claude mcp add draftlize -s user',
    '  -e DRAFTLIZE_DATABASE_URL=postgres://draftlize:draftlize@localhost:5533/draftlize_v4',
    '  -- /path/to/draftlize-mcp',
  ].join(' \\\n');

  const mcpStatusText = (() => {
    if (!status) return t('settings.statusUnavailable');
    if (!status.enabled) return t('settings.statusDisabled');
    if (status.recent_count_24h === 0) return t('settings.statusSetUp');
    return t('settings.statusConnected', { count: status.recent_count_24h });
  })();
  const mcpStatusDot = !status
    ? 'var(--ink-3)'
    : !status.enabled
      ? 'var(--ink-3)'
      : status.recent_count_24h > 0
        ? '#3a8e5a'
        : 'var(--amber, #c98a2b)';

  const stepLabel = (n: number, text: string) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        marginBottom: 6,
      }}
    >
      <span
        className="mono"
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '.12em',
          color: 'var(--ink-3)',
        }}
      >
        STEP {n}
      </span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-0)' }}>
        {text}
      </span>
    </div>
  );

  const cmdLine = (cmd: string, key: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <pre
        className="mono"
        style={{
          flex: 1,
          margin: 0,
          padding: '6px 8px',
          fontSize: 11,
          background: 'var(--paper-1)',
          border: '1px solid var(--rule)',
          borderRadius: 4,
          color: 'var(--ink-1)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          lineHeight: 1.5,
        }}
      >
        {cmd}
      </pre>
      <button
        onClick={() => onCopy(cmd, key)}
        className="mono"
        title={t('settings.copy')}
        style={{
          fontSize: 11,
          padding: '5px 9px',
          border: '1px solid var(--rule)',
          borderRadius: 4,
          background: copied === key ? 'var(--paper-1)' : 'white',
          color: 'var(--ink-1)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        {copied === key ? '✓' : '⧉'}
      </button>
    </div>
  );

  return (
    <section>
      <SectionLabel>{t('settings.wizardTitle')}</SectionLabel>

      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 14, lineHeight: 1.5 }}>
        {t('settings.wizardBlurb')}
      </div>

      {/* ── Step 1: token ─────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        {stepLabel(1, t('settings.wizardStep1'))}
        {created ? (
          <div
            style={{
              padding: '10px 12px',
              border: '1px solid var(--amber, #c98a2b)',
              background: 'rgba(201, 138, 43, 0.08)',
              borderRadius: 4,
            }}
          >
            <div style={{ fontSize: 11.5, color: 'var(--ink-1)', marginBottom: 6 }}>
              <Trans
                i18nKey="settings.tokenCreatedNotice"
                values={{ name: created.name }}
                components={{ name: <strong /> }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code
                className="mono"
                style={{
                  flex: 1,
                  fontSize: 11,
                  padding: '6px 8px',
                  background: 'white',
                  border: '1px solid var(--rule)',
                  borderRadius: 3,
                  color: 'var(--ink-1)',
                  wordBreak: 'break-all',
                }}
              >
                {created.raw_token}
              </code>
              <button
                onClick={() => onCopy(created.raw_token, 'wz-raw')}
                className="mono"
                style={{
                  fontSize: 11,
                  padding: '6px 12px',
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  background: copied === 'wz-raw' ? 'var(--paper-1)' : 'white',
                  color: 'var(--ink-1)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {copied === 'wz-raw' ? t('settings.copied') : t('settings.copyToken')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder={t('settings.tokenNamePlaceholder')}
                disabled={creating}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void createToken();
                }}
                style={{
                  flex: 1,
                  fontSize: 12,
                  padding: '5px 8px',
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  background: 'white',
                  color: 'var(--ink-1)',
                }}
              />
              <button
                onClick={createToken}
                disabled={creating || !tokenName.trim()}
                className="mono"
                style={{
                  fontSize: 11,
                  padding: '5px 14px',
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  background: tokenName.trim() && !creating ? 'var(--paper-1)' : 'white',
                  color: 'var(--ink-1)',
                  cursor: tokenName.trim() && !creating ? 'pointer' : 'not-allowed',
                }}
              >
                {creating ? t('settings.creating') : `+ ${t('settings.wizardGenerate')}`}
              </button>
            </div>
            {liveTokens.length > 0 && (
              <div style={{ marginTop: 5, fontSize: 11, color: 'var(--ink-3)' }}>
                {t('settings.wizardHaveTokens', { count: liveTokens.length })}
              </div>
            )}
          </>
        )}
        {wzError && (
          <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--red, #d23f3f)' }}>
            {wzError}
          </div>
        )}
      </div>

      {/* ── Step 2: install + connect ─────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        {stepLabel(2, t('settings.wizardStep2'))}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {cmdLine(installCmd, 'wz-install')}
          {cmdLine(tokenCmd, 'wz-token')}
          {cmdLine(linkCmd, 'wz-link')}
          {cmdLine(pullCmd, 'wz-pull')}
        </div>
        <div
          style={{
            marginTop: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={() => onCopy(allCmds, 'wz-all')}
            className="mono"
            style={{
              fontSize: 11,
              padding: '4px 12px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: copied === 'wz-all' ? 'var(--paper-1)' : 'white',
              color: 'var(--ink-1)',
              cursor: 'pointer',
            }}
          >
            {copied === 'wz-all' ? t('settings.copied') : t('settings.wizardCopyAll')}
          </button>
          <span style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
            {t('settings.wizardStep2Hint')}
          </span>
        </div>
      </div>

      {/* ── Step 3: live status ───────────────────────────────────── */}
      <div style={{ marginBottom: 18 }}>
        {stepLabel(3, t('settings.wizardStep3'))}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '9px 12px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background:
              connState === 'connected' ? 'rgba(58, 142, 90, 0.07)' : 'var(--paper-1)',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 8,
              flexShrink: 0,
              background:
                connState === 'connected'
                  ? '#3a8e5a'
                  : connState === 'waiting' || connState === 'idle'
                    ? 'var(--amber, #c98a2b)'
                    : 'var(--ink-3)',
            }}
          />
          <span style={{ fontSize: 12, color: 'var(--ink-1)', lineHeight: 1.5 }}>
            {connState === 'loading' && '…'}
            {connState === 'no-token' && t('settings.wizardNoToken')}
            {connState === 'waiting' && t('settings.wizardWaiting')}
            {connState === 'connected' &&
              t('settings.wizardConnected', {
                name: lastUsedToken?.name ?? '',
                time: relativeTime(lastUsedToken?.last_used_at as string),
              })}
            {connState === 'idle' &&
              t('settings.wizardIdle', {
                time: relativeTime(lastUsedToken?.last_used_at as string),
              })}
          </span>
        </div>
        {connState === 'connected' && (
          <div style={{ marginTop: 5, fontSize: 11, color: 'var(--ink-3)' }}>
            <Trans
              i18nKey="settings.wizardTryNext"
              components={{
                code: <code className="mono" style={{ fontSize: 10.5 }} />,
              }}
            />
          </div>
        )}
      </div>

      {/* ── local-dev MCP path (collapsed) ─────────────────────────── */}
      <details style={{ borderTop: '1px dashed var(--rule)', paddingTop: 10 }}>
        <summary
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '.06em',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {t('settings.wizardDevMcp')}
        </summary>
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11.5, color: 'var(--ink-2)', marginBottom: 8, lineHeight: 1.5 }}>
            {t('settings.wizardDevMcpBlurb')}
          </div>

          <Row label={t('settings.rowStatus')}>
            <span
              style={{
                display: 'inline-block',
                width: 7, height: 7, borderRadius: 7, marginRight: 6,
                background: mcpStatusDot,
              }}
            />
            <span style={{ fontSize: 12, color: 'var(--ink-1)' }}>{mcpStatusText}</span>
          </Row>

          <Row label={t('settings.rowProjectId')}>
            <code
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--ink-1)',
                background: 'var(--paper-1)',
                padding: '2px 6px',
                borderRadius: 3,
                border: '1px solid var(--rule)',
                wordBreak: 'break-all',
              }}
            >
              {project.id}
            </code>
            <button
              onClick={() => onCopy(project.id, 'id')}
              className="mono"
              style={{
                marginLeft: 8,
                fontSize: 11,
                padding: '4px 10px',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background: copied === 'id' ? 'var(--paper-1)' : 'white',
                color: 'var(--ink-1)',
                cursor: 'pointer',
              }}
            >
              {copied === 'id' ? t('settings.copied') : t('settings.copy')}
            </button>
          </Row>

          <div style={{ marginTop: 10, marginBottom: 4 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.04em', marginBottom: 4 }}>
              {t('settings.oneTimeInstall')}
            </div>
            <pre
              className="mono"
              style={{
                margin: 0,
                padding: '10px 12px',
                fontSize: 11,
                background: 'var(--paper-1)',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                color: 'var(--ink-1)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                lineHeight: 1.6,
              }}
            >
              {mcpCommand}
            </pre>
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => onCopy(mcpCommand, 'command')}
                className="mono"
                style={{
                  fontSize: 11,
                  padding: '4px 12px',
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  background: copied === 'command' ? 'var(--paper-1)' : 'white',
                  color: 'var(--ink-1)',
                  cursor: 'pointer',
                }}
              >
                {copied === 'command' ? t('settings.copied') : t('settings.copyCommand')}
              </button>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                <Trans
                  i18nKey="settings.buildBinaryHint"
                  components={{
                    code: <code className="mono" style={{ fontSize: 10.5 }} />,
                  }}
                />
              </span>
            </div>
          </div>

          {/* Recent access log — newest 20 events. Empty state covers two
              cases: the binary isn't installed yet (most common) or the
              access log table is empty for some other reason. We don't
              distinguish — both look like "no reads yet" to the user. */}
          <div style={{ marginTop: 14 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.04em', marginBottom: 4 }}>
              {t('settings.recentAccess')}
            </div>
            {status && status.recent_events.length > 0 ? (
              <div
                style={{
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  background: 'white',
                  maxHeight: 180,
                  overflowY: 'auto',
                }}
              >
                {status.recent_events.map((evt) => (
                  <AccessRow key={evt.id} evt={evt} />
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: 11.5,
                  color: 'var(--ink-3)',
                  fontStyle: 'italic',
                  background: 'var(--paper-1)',
                  border: '1px dashed var(--rule)',
                  borderRadius: 4,
                }}
              >
                {t('settings.noExternalReads')}
              </div>
            )}
          </div>

          <Row label="">
            <button
              onClick={onToggleEnabled}
              disabled={toggling || !status}
              className="mono"
              style={{
                fontSize: 11,
                padding: '4px 12px',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background: status?.enabled ? 'white' : 'var(--paper-1)',
                color: 'var(--ink-1)',
                cursor: toggling || !status ? 'not-allowed' : 'pointer',
                marginTop: 8,
              }}
              title={
                status?.enabled
                  ? t('settings.toggleDisableTitle')
                  : t('settings.toggleEnableTitle')
              }
            >
              {toggling
                ? '…'
                : status?.enabled
                  ? `⏻ ${t('settings.toggleDisable')}`
                  : `⏻ ${t('settings.toggleEnable')}`}
            </button>
          </Row>
        </div>
      </details>
    </section>
  );
}

/**
 * Settings → API Tokens section. Lists every long-lived bearer token the
 * current user has minted, with a "Create new" button that pops a small
 * inline form → on submit shows the raw token ONCE in a flash card with
 * a copy button + "save it now, you won't see it again" warning. Backed
 * by the PR-04 endpoints (`/api/tokens` POST/GET, `/api/tokens/{id}`
 * DELETE). The CLI (`draftlize set-token`) and external MCP clients
 * consume these.
 *
 * Wraps a fetch-on-mount + revoke / create flows in local state. No
 * loading prop comes from the parent — the section owns its own
 * lifecycle since the parent doesn't need to know about token state.
 */
function ApiTokensSection({
  copied,
  onCopy,
}: {
  copied: string | null;
  onCopy: (text: string, kind: string) => void;
}) {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  // The freshly-created token. Persisted in this state (not the list) so
  // the secret is rendered exactly once — when the user navigates away or
  // dismisses the flash card it's gone forever (only the sha256 lives in
  // PG). The list refresh below fills the row in `tokens` without the
  // raw secret.
  const [created, setCreated] = useState<CreateApiTokenResponse | null>(null);
  // Two-stage revoke: first click marks the row's id as "armed" and the
  // button flips to a destructive "really revoke?" label; second click
  // fires the DELETE. Mirrors the project-delete pattern above.
  const [revokeArmed, setRevokeArmed] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const rows = await api.listApiTokens();
      setTokens(rows);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const resp = await api.createApiToken(trimmed);
      setCreated(resp);
      setName('');
      void refresh();
    } catch (e) {
      setLoadError(t('settings.errCreateToken', { error: (e as Error).message }));
    } finally {
      setCreating(false);
    }
  };

  const doRevoke = async (id: string) => {
    setRevoking(id);
    try {
      await api.revokeApiToken(id);
      setRevokeArmed(null);
      void refresh();
    } catch (e) {
      setLoadError(t('settings.errRevokeToken', { error: (e as Error).message }));
    } finally {
      setRevoking(null);
    }
  };

  const liveTokens = (tokens ?? []).filter((t) => !t.revoked_at);

  return (
    <section>
      <SectionLabel>{t('settings.sectionTokens')}</SectionLabel>

      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10, lineHeight: 1.5 }}>
        <Trans
          i18nKey="settings.tokensBlurb"
          components={{
            code: <code className="mono" style={{ fontSize: 11 }} />,
          }}
        />
      </div>

      {/* Freshly-created token flash card. Only rendered between the
          create POST and the user dismissing it. The raw secret lives
          ONLY in this card — refreshing or dismissing loses it forever.
          Render before the list so it's the first thing the user's eye
          lands on after the network call returns. */}
      {created && (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            border: '1px solid var(--amber, #c98a2b)',
            background: 'rgba(201, 138, 43, 0.08)',
            borderRadius: 4,
          }}
        >
          <div style={{ fontSize: 11.5, color: 'var(--ink-1)', marginBottom: 6 }}>
            <Trans
              i18nKey="settings.tokenCreatedNotice"
              values={{ name: created.name }}
              components={{ name: <strong /> }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
            }}
          >
            <code
              className="mono"
              style={{
                flex: 1,
                fontSize: 11,
                padding: '6px 8px',
                background: 'white',
                border: '1px solid var(--rule)',
                borderRadius: 3,
                color: 'var(--ink-1)',
                wordBreak: 'break-all',
              }}
            >
              {created.raw_token}
            </code>
            <button
              onClick={() => onCopy(created.raw_token, `token-raw-${created.id}`)}
              className="mono"
              style={{
                fontSize: 11,
                padding: '6px 12px',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background:
                  copied === `token-raw-${created.id}` ? 'var(--paper-1)' : 'white',
                color: 'var(--ink-1)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {copied === `token-raw-${created.id}` ? t('settings.copied') : t('settings.copyToken')}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-2)', marginBottom: 6 }}>
            {t('settings.pasteIntoShell')}
          </div>
          <pre
            className="mono"
            style={{
              margin: 0,
              padding: '6px 8px',
              fontSize: 11,
              background: 'white',
              border: '1px solid var(--rule)',
              borderRadius: 3,
              color: 'var(--ink-1)',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
            }}
          >
            {`draftlize set-token ${created.raw_token}`}
          </pre>
          <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
            <button
              onClick={() =>
                onCopy(`draftlize set-token ${created.raw_token}`, `token-cmd-${created.id}`)
              }
              className="mono"
              style={{
                fontSize: 11,
                padding: '4px 10px',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background:
                  copied === `token-cmd-${created.id}` ? 'var(--paper-1)' : 'white',
                color: 'var(--ink-1)',
                cursor: 'pointer',
              }}
            >
              {copied === `token-cmd-${created.id}` ? t('settings.copied') : t('settings.copyCommand')}
            </button>
            <button
              onClick={() => setCreated(null)}
              className="mono"
              style={{
                fontSize: 11,
                padding: '4px 10px',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background: 'white',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              {t('settings.savedDismiss')}
            </button>
          </div>
        </div>
      )}

      {/* Inline create form. We keep it visible at all times (rather than
          hiding behind a "+ New token" button) — the flow is short
          enough that another click is just friction. */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('settings.tokenNamePlaceholder')}
          disabled={creating}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submitCreate();
          }}
          style={{
            flex: 1,
            fontSize: 12,
            padding: '5px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: 'white',
            color: 'var(--ink-1)',
          }}
        />
        <button
          onClick={submitCreate}
          disabled={creating || !name.trim()}
          className="mono"
          style={{
            fontSize: 11,
            padding: '5px 14px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: name.trim() && !creating ? 'var(--paper-1)' : 'white',
            color: 'var(--ink-1)',
            cursor: name.trim() && !creating ? 'pointer' : 'not-allowed',
          }}
        >
          {creating ? t('settings.creating') : `+ ${t('settings.createToken')}`}
        </button>
      </div>

      {loadError && (
        <div
          style={{
            padding: '6px 10px',
            fontSize: 11.5,
            color: 'var(--red, #d23f3f)',
            marginBottom: 8,
          }}
        >
          {loadError}
        </div>
      )}

      {/* Token list. Empty state covers two cases — first-time users
          and the freshly-revoked-the-last-one case — both indistinguishable
          to the user, both correctly described as "no tokens yet". */}
      {tokens === null ? (
        <div
          style={{
            padding: '8px 10px',
            fontSize: 11.5,
            color: 'var(--ink-3)',
            fontStyle: 'italic',
          }}
        >
          {t('common.loading')}
        </div>
      ) : liveTokens.length === 0 ? (
        <div
          style={{
            padding: '10px 12px',
            fontSize: 11.5,
            color: 'var(--ink-3)',
            fontStyle: 'italic',
            background: 'var(--paper-1)',
            border: '1px dashed var(--rule)',
            borderRadius: 4,
          }}
        >
          {t('settings.noTokens')}
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: 'white',
          }}
        >
          {liveTokens.map((token) => (
            <div
              key={token.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderBottom: '1px solid var(--paper-1)',
                fontSize: 11.5,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: 'var(--ink-1)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {token.name}
                </div>
                <div
                  className="mono"
                  style={{ fontSize: 10.5, color: 'var(--ink-3)' }}
                >
                  {token.prefix}…  ·  {t('settings.tokenCreated')}{' '}
                  {new Date(token.created_at).toLocaleDateString()}
                  {token.last_used_at && (
                    <>
                      {' '}
                      ·  {t('settings.tokenLastUsed', {
                        time: relativeTime(token.last_used_at),
                      })}
                    </>
                  )}
                  {!token.last_used_at && <> ·  {t('settings.tokenNeverUsed')}</>}
                </div>
              </div>
              {revokeArmed === token.id ? (
                <>
                  <button
                    onClick={() => doRevoke(token.id)}
                    disabled={revoking === token.id}
                    className="mono"
                    style={{
                      fontSize: 11,
                      padding: '4px 10px',
                      border: '1px solid var(--red, #d23f3f)',
                      borderRadius: 4,
                      background: 'var(--red, #d23f3f)',
                      color: 'white',
                      cursor: revoking === token.id ? 'progress' : 'pointer',
                    }}
                  >
                    {revoking === token.id ? t('settings.revoking') : t('settings.reallyRevoke')}
                  </button>
                  <button
                    onClick={() => setRevokeArmed(null)}
                    disabled={revoking === token.id}
                    className="mono"
                    style={{
                      fontSize: 11,
                      padding: '4px 10px',
                      border: '1px solid var(--rule)',
                      borderRadius: 4,
                      background: 'white',
                      color: 'var(--ink-2)',
                      cursor: 'pointer',
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setRevokeArmed(token.id)}
                  className="mono"
                  style={{
                    fontSize: 11,
                    padding: '4px 10px',
                    border: '1px solid var(--rule)',
                    borderRadius: 4,
                    background: 'white',
                    color: 'var(--ink-2)',
                    cursor: 'pointer',
                  }}
                >
                  {t('settings.revoke')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** One row in the recent-access table: tool · target · client · relative
 *  time. Compact (~22px) so 20 fit without dominating the modal. */
function AccessRow({ evt }: { evt: import('../api').McpAccessEvent }) {
  const { t } = useTranslation();
  const target = evt.card_id ?? '—';
  const client = evt.client_name ?? t('settings.unknownClient');
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 10px',
        borderBottom: '1px solid var(--paper-1)',
        fontSize: 11,
      }}
    >
      <span
        className="mono"
        style={{
          width: 96,
          color: 'var(--ink-1)',
          flexShrink: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {evt.tool_name}
      </span>
      <span
        className="mono"
        style={{
          width: 72,
          color: 'var(--ink-2)',
          flexShrink: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {target}
      </span>
      <span
        style={{
          flex: 1,
          color: 'var(--ink-2)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {client}
      </span>
      <span
        className="mono"
        style={{ color: 'var(--ink-3)', fontSize: 10, flexShrink: 0 }}
        title={evt.ts}
      >
        {relativeTime(evt.ts)}
      </span>
    </div>
  );
}

/** 计费重构（2026-06-28）：Settings → Billing 区，预付费钱包视图。
 *
 *  订阅制（doc/07 PR-33 的 trial / portal / 5h 滚动配额）已全部废弃。
 *  现在这里展示：
 *    - 顶部大号当前余额（me.wallet.balance_cents/100，余额 < $1 用 amber）
 *    - 「充值」按钮 → 打开共享的 <TopupModal>（Stripe 一次性 Checkout）
 *    - 交易历史：mount 时拉 /api/wallet/transactions，每条左侧 kind 标签 +
 *      日期，右侧金额（amount_tmc/1_000_000，进项绿色带 +，出项默认色）
 *
 *  金额单位说明：余额是 cents（÷100 得美元）；流水是 tmc（÷1_000_000 得
 *  美元），后者精度更高以显示 sub-cent 的单次消费。
 */
function BillingSection({ me }: { me: MeResponse | null }) {
  const { t } = useTranslation();
  const [topupOpen, setTopupOpen] = useState(false);
  const [txns, setTxns] = useState<WalletTransaction[] | null>(null);
  const [wallet, setWallet] = useState<WalletStatus | null>(null);

  useEffect(() => {
    let alive = true;
    // 实时拉余额 + 交易：打开账单即最新，不依赖启动时的 me.wallet 快照，
    // 这样消费扣费后账单立刻反映（不必刷新页面）。
    api.getWallet().then(
      (w) => {
        if (alive) setWallet(w);
      },
      () => {
        /* best-effort：失败时回退到 me.wallet 快照 */
      },
    );
    api.getWalletTransactions().then(
      (r) => {
        if (alive) setTxns(r);
      },
      () => {
        // Best-effort — an empty list reads the same as "no transactions",
        // which is the right fallback when the fetch fails.
        if (alive) setTxns([]);
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  if (!me) {
    return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>;
  }

  // 实时余额优先，拉取完成前回退到 me.wallet 快照。
  const balanceUsd = (wallet?.balance_cents ?? me.wallet.balance_cents) / 100;
  const lowBalance = balanceUsd < 1;

  return (
    <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
      {/* Balance hero */}
      <div
        style={{
          padding: '18px 18px 16px',
          background: lowBalance ? 'var(--amber-soft)' : 'var(--paper-1)',
          border: `1px solid ${lowBalance ? 'var(--amber)' : 'var(--rule)'}`,
          borderRadius: 4,
          marginBottom: 16,
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 10,
            letterSpacing: '.14em',
            color: 'var(--ink-2)',
            marginBottom: 6,
          }}
        >
          {t('billing.balance').toUpperCase()}
        </div>
        <div
          className="serif"
          style={{
            fontSize: 38,
            lineHeight: 1.05,
            fontWeight: 600,
            color: lowBalance ? 'var(--amber)' : 'var(--ink-0)',
          }}
        >
          ${balanceUsd.toFixed(2)}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        <button onClick={() => setTopupOpen(true)} className="mono" style={primaryBtn()}>
          {t('billing.topUp')}
        </button>
      </div>

      {/* Prepaid explainer */}
      <div
        style={{
          fontSize: 12,
          color: 'var(--ink-2)',
          lineHeight: 1.6,
          marginBottom: 22,
        }}
      >
        {t('billing.prepaidExplainer')}
      </div>

      {/* Transactions */}
      <div
        className="mono"
        style={{
          fontSize: 10,
          letterSpacing: '.14em',
          color: 'var(--ink-2)',
          paddingBottom: 8,
          borderBottom: '1px solid var(--rule)',
          marginBottom: 4,
        }}
      >
        {t('billing.transactions').toUpperCase()}
      </div>

      {txns == null ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12.5, padding: '14px 0' }}>
          {t('common.loading')}
        </div>
      ) : txns.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12.5, padding: '14px 0' }}>
          {t('billing.noTransactions')}
        </div>
      ) : (
        <div>
          {txns.map((tx) => (
            <TransactionRow key={tx.id} tx={tx} />
          ))}
        </div>
      )}

      <TopupModal open={topupOpen} onClose={() => setTopupOpen(false)} />
    </div>
  );
}

/** One row in the wallet transaction history. Left: kind label + date.
 *  Right: amount in USD (amount_tmc ÷ 1_000_000) — a credit (signup bonus,
 *  top-up, refund; amount > 0) renders green with a leading "+", a debit
 *  (usage; amount < 0) renders in the default ink colour. */
function TransactionRow({ tx }: { tx: WalletTransaction }) {
  const { t } = useTranslation();
  const usd = tx.amount_tmc / 1_000_000;
  const credit = tx.amount_tmc > 0;
  // Unknown kinds fall back to the raw string so a new server-side kind
  // still renders something readable rather than a missing-key marker.
  const label = t(`billing.txn.${tx.kind}`, { defaultValue: tx.kind });
  const dateLabel = fmtTxnDate(tx.created_at);
  // Two decimals usually, but tiny per-turn spends round to $0.00 there —
  // widen to enough places to show a non-zero figure for sub-cent amounts.
  const abs = Math.abs(usd);
  const decimals = abs > 0 && abs < 0.01 ? 4 : 2;
  const amountStr = `${credit ? '+' : '-'}$${abs.toFixed(decimals)}`;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid var(--rule-s)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ color: 'var(--ink-1)', fontSize: 13 }}>{label}</span>
        <span
          className="mono"
          style={{ color: 'var(--ink-3)', fontSize: 10.5, letterSpacing: '.04em' }}
          title={tx.created_at}
        >
          {dateLabel}
        </span>
      </div>
      <span
        className="mono"
        style={{
          fontWeight: 700,
          fontSize: 13,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          color: credit ? 'var(--green)' : 'var(--ink-1)',
        }}
      >
        {amountStr}
      </span>
    </div>
  );
}

/** Absolute date for a transaction row (locale-aware, with a graceful
 *  fallback to the raw ISO string). */
function fmtTxnDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function primaryBtn(disabled = false): React.CSSProperties {
  return {
    fontSize: 11,
    letterSpacing: '.12em',
    padding: '10px 16px',
    border: '1px solid var(--ink-0)',
    background: disabled ? 'var(--ink-3)' : 'var(--ink-0)',
    color: 'var(--paper-0)',
    cursor: disabled ? 'wait' : 'pointer',
    fontWeight: 700,
  };
}

/** "2m ago" / "3h ago" / "yesterday" / "5d ago". Prefer human-readable
 *  over absolute timestamps in dense lists; the absolute time stays in
 *  the title attr for hover. */
function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return i18n.t('settings.relSeconds', { count: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return i18n.t('settings.relMinutes', { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return i18n.t('settings.relHours', { count: hr });
  const day = Math.floor(hr / 24);
  if (day === 1) return i18n.t('settings.relYesterday');
  return i18n.t('settings.relDays', { count: day });
}

/** Invites tab (doc/12 §3.1.3). Fetching IS the grant trigger: the
 *  server lazily mints the user-grant batch when the user first
 *  qualifies (7 days tenure + ≥20 non-gap cards), so this tab needs no
 *  "claim" button — codes either exist, or the empty state explains how
 *  to earn them. */
function InvitesSection() {
  const { t } = useTranslation();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.myInvites>> | null>(null);
  const [error, setError] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    api.myInvites()
      .then(setData)
      .catch(() => setError(true));
  }, []);

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1500);
    } catch {
      // Clipboard denied (http / permissions) — the code is visible,
      // the user can select it by hand.
    }
  };

  if (error) {
    return (
      <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
        {t('settings.invitesLoadFailed')}
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
        {t('settings.loading')}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--ink-1)' }}>
        {t('settings.invitesIntro')}
      </div>
      {data.granted_now && (
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'var(--ink-1)',
            background: 'var(--paper-1)',
            border: '1px solid var(--rule)',
            borderRadius: 6,
            padding: '10px 12px',
          }}
        >
          {t('settings.invitesGrantedNow')}
        </div>
      )}
      {data.codes.length === 0 ? (
        <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--ink-3)' }}>
          {t('settings.invitesEmpty')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.codes.map((c) => (
            <div
              key={c.code}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                border: '1px solid var(--rule)',
                borderRadius: 6,
                background: c.used || c.revoked ? 'var(--paper-1)' : 'white',
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 14,
                  letterSpacing: '.12em',
                  color: c.used || c.revoked ? 'var(--ink-3)' : 'var(--ink-0)',
                  textDecoration: c.revoked ? 'line-through' : 'none',
                  flex: 1,
                }}
              >
                {c.code}
              </span>
              {c.revoked ? (
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {t('settings.inviteRevoked')}
                </span>
              ) : c.used ? (
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {t('settings.inviteUsed')}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => copy(c.code)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--rule)',
                    borderRadius: 5,
                    padding: '4px 10px',
                    fontSize: 11.5,
                    fontFamily: 'inherit',
                    color: 'var(--ink-1)',
                    cursor: 'pointer',
                  }}
                >
                  {copiedCode === c.code
                    ? t('settings.inviteCopied')
                    : t('settings.inviteCopy')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
